/**
 * A simplified iCalendar parser for browser environments
 */

/**
 * Unescapes iCalendar text values according to RFC 5545
 * @param {string} text - The text to unescape
 * @returns {string} The unescaped text
 */
function unescapeICalText(text) {
    if (!text) return text;
    
    return text
        .replace(/\\n/g, '\n')      // Newlines
        .replace(/\\r/g, '\r')      // Carriage returns
        .replace(/\\t/g, '\t')      // Tabs
        .replace(/\\,/g, ',')       // Commas
        .replace(/\\;/g, ';')       // Semicolons
        .replace(/\\\\/g, '\\');    // Backslashes (must be last)
}

export function parseICS(icsString) {
    // Validate input
    if (!icsString || typeof icsString !== 'string') {
        throw new Error("Invalid iCalendar file: empty or invalid content");
    }

    // Normalize line endings and unfold lines
    const unfolded = icsString.replace(/\r\n[ \t]/g, "").replace(/\r/g, "\n");

    // Split into lines
    const lines = unfolded.split("\n").filter(line => line.trim() !== "");

    // Verify this is an iCalendar file
    if (lines.length === 0 || !lines[0].includes("BEGIN:VCALENDAR")) {
        throw new Error("Not a valid iCalendar file");
    }

    // Create structure for parsing
    let events = [];
    let currentEvent = null;

    // Parse the lines
    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];

        // Handle event start
        if (line.includes("BEGIN:VEVENT")) {
            currentEvent = {
                type: "VEVENT",
                start: null,
                end: null,
                summary: "",
                description: "",
                location: "",
                url: "",
            };
            continue;
        }

        // Handle event end
        if (line.includes("END:VEVENT")) {
            if (currentEvent) {
                events.push(currentEvent);
            }
            currentEvent = null;
            continue;
        }

        // Skip if not in an event
        if (!currentEvent) continue;

        // Parse event properties
        const colonPos = line.indexOf(":");
        if (colonPos === -1) continue;

        const propName = line.substring(0, colonPos).split(";")[0];
        const propValue = line.substring(colonPos + 1);

        switch (propName) {
            case "UID":
                currentEvent.id = propValue;
                break;
            case "SUMMARY":
                currentEvent.summary = unescapeICalText(propValue);
                break;
            case "DESCRIPTION":
                currentEvent.description = unescapeICalText(propValue);
                break;
            case "LOCATION":
                currentEvent.location = unescapeICalText(propValue);
                break;
            case "URL":
                currentEvent.url = propValue; // URLs shouldn't need unescaping
                break;
            case "DTSTART":
                currentEvent.start = parseICalDate(propValue);
                break;
            case "DTEND":
                currentEvent.end = parseICalDate(propValue);
                break;
            default:
                break;
        }
    }

    return events;
}

// Helper function to parse iCalendar dates
function parseICalDate(dateString) {
    // Validate input
    if (!dateString || typeof dateString !== 'string') {
        return null;
    }

    // Format: YYYYMMDDTHHMMSSZ (UTC)
    if (dateString.match(/^\d{8}T\d{6}Z$/)) {
        const year = parseInt(dateString.substring(0, 4));
        const month = parseInt(dateString.substring(4, 6)) - 1;
        const day = parseInt(dateString.substring(6, 8));
        const hour = parseInt(dateString.substring(9, 11));
        const minute = parseInt(dateString.substring(11, 13));
        const second = parseInt(dateString.substring(13, 15));

        return new Date(Date.UTC(year, month, day, hour, minute, second));
    }

    // Format: YYYYMMDDTHHMMSS (local time)
    if (dateString.match(/^\d{8}T\d{6}$/)) {
        const year = parseInt(dateString.substring(0, 4));
        const month = parseInt(dateString.substring(4, 6)) - 1;
        const day = parseInt(dateString.substring(6, 8));
        const hour = parseInt(dateString.substring(9, 11));
        const minute = parseInt(dateString.substring(11, 13));
        const second = parseInt(dateString.substring(13, 15));

        return new Date(year, month, day, hour, minute, second);
    }

    // Format: YYYYMMDD (just date)
    if (dateString.match(/^\d{8}$/)) {
        const year = parseInt(dateString.substring(0, 4));
        const month = parseInt(dateString.substring(4, 6)) - 1;
        const day = parseInt(dateString.substring(6, 8));

        return new Date(year, month, day);
    }

    // For other formats, try using Date directly
    return new Date(dateString);
}
